import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { PanelLeft } from "lucide-react";

import { cn } from "@/lib/utils";
import { useIsMobile } from "../../hooks/use-mobile";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SIDEBAR_WIDTH = "248px";
const SIDEBAR_WIDTH_ICON = "72px";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface AppSidebarContextValue {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  isMobile: boolean;
}

const AppSidebarContext = React.createContext<AppSidebarContextValue | null>(null);

export const useAppSidebar = () => {
  const context = React.useContext(AppSidebarContext);
  if (!context) {
    throw new Error("useAppSidebar must be used within an AppSidebarProvider");
  }
  return context;
};

// ---------------------------------------------------------------------------
// AppSidebarProvider
// ---------------------------------------------------------------------------

export interface AppSidebarProviderProps {
  children: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const AppSidebarProvider = ({
  children,
  defaultOpen = true,
  open: openProp,
  onOpenChange
}: AppSidebarProviderProps) => {
  const isMobile = useIsMobile();
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);

  const open = openProp ?? internalOpen;

  const setOpen = React.useCallback(
    (value: boolean) => {
      if (onOpenChange) {
        onOpenChange(value);
      } else {
        setInternalOpen(value);
      }
    },
    [onOpenChange]
  );

  const toggleSidebar = React.useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);

  const state: "expanded" | "collapsed" = open ? "expanded" : "collapsed";

  const value = React.useMemo(
    () => ({ state, open, setOpen, toggleSidebar, isMobile }),
    [state, open, setOpen, toggleSidebar, isMobile]
  );

  return (
    <AppSidebarContext.Provider value={value}>
      <TooltipProvider delayDuration={0}>
        <div
          className="group/sidebar-wrapper flex min-h-svh w-full"
          data-state={state}
          data-collapsible={state === "collapsed" ? "icon" : undefined}
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON
            } as React.CSSProperties
          }
        >
          {children}
        </div>
      </TooltipProvider>
    </AppSidebarContext.Provider>
  );
};
AppSidebarProvider.displayName = "AppSidebarProvider";

// ---------------------------------------------------------------------------
// AppSidebar
// ---------------------------------------------------------------------------

export interface AppSidebarProps extends React.ComponentProps<"aside"> {
  collapsible?: "icon" | "none";
}

export const AppSidebar = React.forwardRef<HTMLElement, AppSidebarProps>(
  ({ className, collapsible = "icon", children, ...props }, ref) => {
    const { state } = useAppSidebar();

    if (collapsible === "none") {
      return (
        <aside
          ref={ref}
          className={cn(
            "sticky top-0 flex h-svh w-[--sidebar-width] flex-col bg-sidebar text-sidebar-foreground",
            className
          )}
          {...props}
        >
          {children}
        </aside>
      );
    }

    return (
      <aside
        ref={ref}
        data-state={state}
        data-collapsible={collapsible}
        className={cn(
          "group/sidebar sticky top-0 flex h-svh flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-linear",
          state === "expanded" ? "w-[--sidebar-width]" : "w-[--sidebar-width-icon]",
          className
        )}
        {...props}
      >
        <div className="flex h-full w-full flex-col overflow-hidden">{children}</div>
      </aside>
    );
  }
);
AppSidebar.displayName = "AppSidebar";

// ---------------------------------------------------------------------------
// AppSidebarTrigger
// ---------------------------------------------------------------------------

export const AppSidebarTrigger = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, children, onClick, ...props }, ref) => {
    const { toggleSidebar } = useAppSidebar();

    return (
      <button
        ref={ref}
        type="button"
        onClick={(e) => {
          onClick?.(e);
          toggleSidebar();
        }}
        className={cn(
          "inline-flex h-7 w-7 items-center justify-center rounded-md text-sidebar-foreground outline-none ring-sidebar-ring transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2",
          className
        )}
        {...props}
      >
        {children ?? <PanelLeft className="h-4 w-4" />}
        <span className="sr-only">Toggle sidebar</span>
      </button>
    );
  }
);
AppSidebarTrigger.displayName = "AppSidebarTrigger";

// ---------------------------------------------------------------------------
// AppSidebarRail
// ---------------------------------------------------------------------------

export const AppSidebarRail = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, ...props }, ref) => {
    const { toggleSidebar } = useAppSidebar();

    return (
      <button
        ref={ref}
        type="button"
        tabIndex={-1}
        aria-label="Toggle sidebar"
        onClick={toggleSidebar}
        className={cn(
          "absolute right-0 top-0 z-20 hidden h-full w-1 cursor-w-resize bg-transparent transition-all after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border sm:flex",
          className
        )}
        {...props}
      />
    );
  }
);
AppSidebarRail.displayName = "AppSidebarRail";

// ---------------------------------------------------------------------------
// Layout Sections
// ---------------------------------------------------------------------------

export const AppSidebarHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("flex flex-col gap-2", className)} {...props} />
);
AppSidebarHeader.displayName = "AppSidebarHeader";

export const AppSidebarContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto", className)} {...props} />
  )
);
AppSidebarContent.displayName = "AppSidebarContent";

export const AppSidebarFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("flex flex-col gap-2 p-2", className)} {...props} />
);
AppSidebarFooter.displayName = "AppSidebarFooter";

export const AppSidebarSeparator = React.forwardRef<HTMLHRElement, React.HTMLAttributes<HTMLHRElement>>(
  ({ className, ...props }, ref) => (
    <hr ref={ref} className={cn("mx-2 w-auto border-sidebar-border", className)} {...props} />
  )
);
AppSidebarSeparator.displayName = "AppSidebarSeparator";

// ---------------------------------------------------------------------------
// Navigation – Group
// ---------------------------------------------------------------------------

export const SidebarGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("relative flex w-full min-w-0 flex-col px-[7px] pt-0 pb-2", className)} {...props} />
  )
);
SidebarGroup.displayName = "SidebarGroup";

export const SidebarGroupLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[state=collapsed]/sidebar-wrapper:sr-only",
        className
      )}
      {...props}
    />
  )
);
SidebarGroupLabel.displayName = "SidebarGroupLabel";

export const SidebarGroupContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("w-full text-sm", className)} {...props} />
);
SidebarGroupContent.displayName = "SidebarGroupContent";

// ---------------------------------------------------------------------------
// Navigation – Menu
// ---------------------------------------------------------------------------

export const SidebarMenu = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn("flex w-full min-w-0 flex-col", className)} {...props} />
  )
);
SidebarMenu.displayName = "SidebarMenu";

export const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.HTMLAttributes<HTMLLIElement>>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      className={cn(
        "group/menu-item relative",
        "group-data-[state=collapsed]/sidebar-wrapper:flex group-data-[state=collapsed]/sidebar-wrapper:justify-center",
        className
      )}
      {...props}
    />
  )
);
SidebarMenuItem.displayName = "SidebarMenuItem";

// ---------------------------------------------------------------------------
// SidebarMenuButton
// ---------------------------------------------------------------------------

export interface SidebarMenuButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  tooltip?: string;
  isActive?: boolean;
  size?: "default" | "lg";
  asChild?: boolean;
}

export const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
  ({ className, tooltip, isActive = false, size = "default", asChild = false, children, ...props }, ref) => {
    const { state, isMobile } = useAppSidebar();
    const Comp = asChild ? Slot : "button";

    const button = (
      <Comp
        ref={ref}
        {...(!asChild && { type: "button" as const })}
        data-active={isActive ? true : undefined}
        className={cn(
          "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md px-2 py-1 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding]",
          "hover:bg-sidebar-accent ",
          "focus-visible:ring-2",
          "disabled:pointer-events-none disabled:opacity-50",
          "[&>svg]:size-4 [&>svg]:shrink-0",
          isActive && "text-[#f97316] font-semibold",
          size === "lg" && "h-12 text-sm",
          // Collapsed: square icon-only button, hide non-icon children
          "group-data-[state=collapsed]/sidebar-wrapper:size-11 group-data-[state=collapsed]/sidebar-wrapper:justify-center group-data-[state=collapsed]/sidebar-wrapper:px-2 group-data-[state=collapsed]/sidebar-wrapper:py-6 group-data-[state=collapsed]/sidebar-wrapper:bg-sidebar",
          "group-data-[state=collapsed]/sidebar-wrapper:[&>*:not(:first-child)]:hidden",
          className
        )}
        {...props}
      >
        {children}
      </Comp>
    );

    if (!tooltip || state !== "collapsed" || isMobile) {
      return button;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="right" align="center">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";

// ---------------------------------------------------------------------------
// SidebarMenuBadge
// ---------------------------------------------------------------------------

export const SidebarMenuBadge = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-medium tabular-nums text-sidebar-foreground",
        "peer-hover/menu-button:text-sidebar-accent-foreground",
        "group-data-[state=collapsed]/sidebar-wrapper:hidden",
        className
      )}
      {...props}
    />
  )
);
SidebarMenuBadge.displayName = "SidebarMenuBadge";

// ---------------------------------------------------------------------------
// Navigation – Sub-menu
// ---------------------------------------------------------------------------

export const SidebarMenuSub = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn(
        "ml-[19px] mr-1 flex min-w-0 translate-x-px flex-col gap-1 border-l border-gray-300 px-2.5 py-0.5",
        "group-data-[state=collapsed]/sidebar-wrapper:hidden",
        className
      )}
      {...props}
    />
  )
);
SidebarMenuSub.displayName = "SidebarMenuSub";

export const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.HTMLAttributes<HTMLLIElement>>(
  ({ className, ...props }, ref) => <li ref={ref} className={cn(className)} {...props} />
);
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

// ---------------------------------------------------------------------------
// SidebarMenuSubButton
// ---------------------------------------------------------------------------

export interface SidebarMenuSubButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isActive?: boolean;
  asChild?: boolean;
}

export const SidebarMenuSubButton = React.forwardRef<HTMLButtonElement, SidebarMenuSubButtonProps>(
  ({ className, isActive = false, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        ref={ref}
        {...(!asChild && { type: "button" as const })}
        data-active={isActive ? true : undefined}
        className={cn(
          "flex h-7 min-w-0 -translate-x-px items-center gap-0 rounded-md py-[22px] pl-2.5 pr-2 text-sidebar-foreground outline-none ring-sidebar-ring",
          "hover:bg-sidebar-accent ",
          "focus-visible:ring-2",
          "active:bg-sidebar-accent ",
          "disabled:pointer-events-none disabled:opacity-50",
          "[&>svg]:size-4 [&>svg]:shrink-0",
          isActive && " text-[#f97316]",
          className
        )}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";
